import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import {AttrsSchema, Component, jsx} from 'panel';
import type {VNode} from 'snabbdom';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {
  InsightsBookmarkParams,
  InsightsDeprecatedLegendUrlData,
  InsightsQueryResults,
} from 'common/report/insights/util/types';
import {MultiMetricChartEl} from 'common/charts/visualization/multi-metric';
import {MetricSegment} from 'common/charts/visualization/multi-metric/types';
import {EmptyDataEl} from 'common/charts/misc/empty-data';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {useStore} from 'common/util/component-helpers';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {SegmentFilterController, SegmentItem} from 'common/charts/contexts/segment-filter-controller';
import {getChartSegmentColorCombo} from 'common/util/colors';
import {ChartType, REPORT_LIMITS, ReportName, RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';
import {
  getFlatSegmentDataTableColumns,
  getFlatSegmentTableRow,
  getSeriesDisplaySegmentColumns,
  getSingleNumberFromSegmentValue,
  getSortConfig,
  isSingleSegmentedMetric,
} from 'common/report/insights/util';
import {getLiftBaselineSegment} from 'common/report/util/lift';
import {ParamsAndResults} from 'common/report/types';
import {PopoverCallback} from 'common/charts/types';
import {ContextMenuEl} from 'common/widgets/context-menu';
import {getOffsetParent} from 'common/util/dom';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {computeMetricChartTooltipSections, computeMetricClauseByKey} from 'common/report/insights/util/tooltip';
import {ChartTooltipAttrs} from 'common/components/chart-tooltip';
import {renamePropertyValue} from 'common/report/util/properties';
import {
  isFunnelConversionMetric,
  isFunnelTimeToConvertMetric,
  isRetentionConversionRateMetric,
} from 'common/report/insights/models/utils';
import {formatMetricNumber, formatPercent} from 'common/util';
import {formatConciseDuration} from 'common/util/date';
import {formatDataTableNumber} from 'common/components/data-table/trend-data/utils';

import css from './index.inline.styl';
import {getDefaultSelectedSegmentLimit, getMetricData, getMetricLabels, InsightsMetricData} from './util';

export interface InsightsMetricJsxProps {
  attrs?: EmptyObj;
  props: {
    // use a single prop for bookmark params and results so they can be updated together atomically
    paramsAndResults: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults> | null;

    // legend data will eventually be persisted in bookmark, URL persistence is a stopgap
    urlLegendData?: InsightsDeprecatedLegendUrlData;

    // dataGroups will automatically be obtained from Stores without this prop,
    // but this prop is available for environments where Stores aren't supported (eg. Public Dashboards)
    dataGroups?: Record<string, DataGroup>;

    popoverCallback?: PopoverCallback;
  };
}

type InsightsMetricState = InsightsMetricData;
export class InsightsMetricChart extends Component<
  InsightsMetricState,
  InsightsMetricJsxProps['attrs'],
  any,
  any,
  ChartsContextRegistry
> {
  static get attrsSchema(): AttrsSchema<InsightsMetricJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
    };
  }

  private _bookmarkParams: InsightsBookmarkParams | null = null;
  public get bookmarkParams(): InsightsBookmarkParams | null {
    return this._bookmarkParams;
  }

  private _results: InsightsQueryResults | null = null;
  public get results(): InsightsQueryResults | null {
    return this._results;
  }

  public set paramsAndResults({
    params,
    results,
  }: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults> | null) {
    if (!isEqual(this.bookmarkParams, params) || !isEqual(this.results, results)) {
      this._bookmarkParams = params;
      this._results = results;
      this.updateChartData();
      this.updatePersistedLegendSelections();
    }
  }

  private _urlLegendData: InsightsDeprecatedLegendUrlData | null = null;
  public get urlLegendData(): InsightsDeprecatedLegendUrlData | null {
    return this._urlLegendData;
  }
  public set urlLegendData(value: InsightsDeprecatedLegendUrlData | null) {
    if (!isEmpty(value)) {
      this._urlLegendData = value;
      this.updatePersistedLegendSelections();
    }
  }

  // optional dataGroups props in case dataGroupStore is not available (e.g. Public Dashboards)
  private _dataGroups: Record<string, DataGroup> | null = null;
  public get dataGroups(): Record<string, DataGroup> | null {
    return this._dataGroups;
  }
  public set dataGroups(value: Record<string, DataGroup> | null) {
    this._dataGroups = value;
    this.updateChartData();
  }

  public popoverCallback: PopoverCallback | null = null;

  get config() {
    return {
      useShadowDom: true,
      css,
      template: () => this.renderTemplate(),
      defaultState: {
        segmentLookup: null,
        legendIdToIndexMap: null,
        legendIndexToId: null,
        timeComparisonLookup: null,
        liftComparisonLookup: null,
        popoverStyles: null,
        popoverSegment: null,
      },
      contexts: [`segmentFilterController` as const, `currentProject` as const],
      defaultContexts: {segmentFilterController: new SegmentFilterController()},
    };
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this._dataGroups) {
      useStore(this, dataGroupStore).fetch({
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
      });
    }

    this.updateChartData();
    this.updatePersistedLegendSelections();
  }

  private getDataGroups(): Record<string, DataGroup> {
    if (this._dataGroups) {
      return this._dataGroups;
    } else {
      return dataGroupStore.value;
    }
  }

  private renderTemplate(): VNode {
    if (isEmpty(this.results?.series)) {
      return (
        <div sel='.root'>
          <EmptyDataEl />
        </div>
      );
    }

    if (!this.hasRequiredParams() || !this.state.segmentLookup) {
      return <div sel='.root' />;
    }

    return (
      <div sel='.root'>
        <MultiMetricChartEl
          attrs={{
            elref: `multiMetric`,
            'lift-type': this.bookmarkParams.liftComparison?.type,
            metrics: this.getMetricSegments(),
            'hide-tooltip': !!this.state.popoverSegment,
            'selected-metric': this.state.popoverSegment,
          }}
          props={{
            onMetricChartClick: this.popoverCallback && this.handleClick,
            metricFormatter: (value) => this.metricFormatter(value),
            tooltipDataCallback: (segment) => this.getTooltipData(segment),
          }}
        />
        {/* render popover */}
        {this.renderPopover()}
      </div>
    );
  }

  renderPopover() {
    const {popoverSegment, popoverStyles} = this.state;

    if (!popoverSegment || !this.popoverCallback) {
      return <></>;
    }

    const segment = popoverSegment.path[popoverSegment.path.length - 1];
    const segments = popoverSegment.path;
    return (
      <ContextMenuEl
        attrs={{
          'menu-items': this.popoverCallback(segment, segments),
          'relative-positioning': true,
          open: true,
          position: {left: 0, top: 0},
        }}
        style={popoverStyles}
        on={{
          action: (ev: CustomEvent) => this.handlePopoverAction(ev),
          close: () => this.handlePopoverClose(),
        }}
      />
    );
  }

  handleClick = (ev, metricSegment) => {
    ev.stopPropagation();

    const {pageX, pageY} = ev;
    const {path} = metricSegment;

    // don't support actions for "Other"
    if (path[path.length - 1] === OTHER_SEGMENT_VALUE || !this.popoverCallback) {
      return;
    }

    // getOffsetParent could return null in some cases
    const parentRect = getOffsetParent(this).getBoundingClientRect();

    const gap = 12;
    const left = pageX - parentRect.x - window.scrollX + gap;
    const top = pageY - parentRect.y - window.scrollY + gap;

    const segment = path[path.length - 1];
    const segments = path;
    const hasPopoverActions = this.popoverCallback?.call(null, segment, segments).length;
    if (hasPopoverActions) {
      this.update({
        popoverStyles: {left: `${left}px`, top: `${top}px`},
        popoverSegment: metricSegment,
      });
    }
  };

  handlePopoverAction(ev: CustomEvent) {
    const {path, color} = this.state.popoverSegment;
    this.dispatchEvent(
      new CustomEvent(`popoverAction`, {
        detail: {segment: path, color, action: ev.detail.action},
      }),
    );
    this.handlePopoverClose();
  }

  handlePopoverClose() {
    this.update({
      popoverStyles: null,
      popoverSegment: null,
    });
  }

  private getTooltipData(segment: MetricSegment): ChartTooltipAttrs {
    const metricClauseByKey = computeMetricClauseByKey({
      sectionsMetadata: this.results.meta.report_sections,
      bookmarkParams: this.bookmarkParams,
      dataGroups: Object.values(this.getDataGroups()),
    });
    const metricKey = segment.path[0];

    return {
      'title-color': segment.color,
      sections: computeMetricChartTooltipSections({
        metricClauseData: metricClauseByKey[metricKey],
        formatter: this.metricFormatter,
        segments: segment.path
          .slice(1)
          .map((value, index) => renamePropertyValue(value, this.results.headers[index + 1])),
        value: segment.value,
      }),
    };
  }

  private hasRequiredParams(): boolean {
    return !!(this.isConnected && this.bookmarkParams && this.results);
  }

  private get segmentFilterController() {
    return this.getContext(`segmentFilterController`);
  }

  private updateChartData() {
    if (this.hasRequiredParams()) {
      const data = getMetricData(this.results);
      this.update(data);

      const segments = Array.from(data.segmentLookup.values()).filter(
        (segment) => !segment.path.includes(RESULTS_OVERALL_SUM_KEY),
      );

      const segmentColumns = getSeriesDisplaySegmentColumns(
        this.bookmarkParams.sections,
        this.results,
        this.getDataGroups(),
      );

      const _isSingleSegmentedMetric = isSingleSegmentedMetric(this.results.headers, this.results.series);
      const legendItems: SegmentItem[] = segments.map((segment, idx) => ({
        id: segment.key,
        // improves readability for the legend when displaying results for single segmented metrics
        label: _isSingleSegmentedMetric ? segment.displayNamePath.slice(1).join(` / `) : segment.displayName,
        color: getChartSegmentColorCombo(idx).primary,
        tableRow: getFlatSegmentTableRow({
          segment,
          timeComparisonSegment: data.timeComparisonLookup?.get(segment.key),
        }),
      }));

      // this.dataGroups exists only for public dashboard. In case of public dashboard, we assign null
      // to projectStore to prevent getContext() from fetching data from /metadata endpoint
      const currentProject = this.dataGroups ? null : this.getContext(`currentProject`).get().value;
      const defaultNumSelections = getDefaultSelectedSegmentLimit(currentProject);
      const maxNumSelections = REPORT_LIMITS[ReportName.Insights].segments[ChartType.Metric].max;

      const {apiResultsTruncated, queryLimits} = this.results.meta;
      this.segmentFilterController.loadSections(
        [
          {
            items: legendItems,
            metadata: data.legendIdToIndexMap,
            tableColumns: getFlatSegmentDataTableColumns({
              segmentColumns,
              hasTimeComparison: !!this.results.time_comparison,
              cellRenderer: ({item, context}) => this.metricFormatter(item[context.field], {forDataTable: true}),
            }),
            apiResultsTruncated,
          },
        ],
        {
          tableSortConfig: getSortConfig({
            bookmarkSortConfig: this.bookmarkParams.sorting,
            headerCount: this.results.headers.length,
          })[ChartType.Metric],
          defaultNumSelections,
          maxNumSelections,
        },
      );
      this.segmentFilterController.segmentationQueryLimit = queryLimits?.limit;
      this.segmentFilterController.exportSegmentationQueryLimit = REPORT_LIMITS[ReportName.Insights].csvExport;
    }
  }

  private updatePersistedLegendSelections(): void {
    const individualSelectedSegments = this.bookmarkParams?.legend?.individualSelectedSegments;
    const indexSelections = this.urlLegendData?.idxSelections;

    if (this.isConnected && (individualSelectedSegments || !indexSelections)) {
      this.segmentFilterController.loadFromSavedLegend(
        individualSelectedSegments ? [individualSelectedSegments] : null,
      );
      return;
    }

    const isFlatSelection =
      Array.isArray(indexSelections) && indexSelections.length > 0 && !Array.isArray(indexSelections[0]);
    if (isFlatSelection && !!this.state.segmentLookup) {
      const selectedIds = (indexSelections as number[]).map((legendIndex) => this.state.legendIndexToId[legendIndex]);

      this.segmentFilterController.loadPersistedSelections([selectedIds], {shouldInvokeToggleCallbacks: true});
    }
  }

  private getMetricSegments(): MetricSegment[] {
    const {segmentLookup, timeComparisonLookup, liftComparisonLookup} = this.state;

    return this.segmentFilterController.getSelectedItems(0).map((item) => {
      const segment = segmentLookup.get(item.id);
      const value = getSingleNumberFromSegmentValue(segment.value);
      const comparisonSegment = timeComparisonLookup?.get(segment.key);
      let comparisonValue = comparisonSegment ? getSingleNumberFromSegmentValue(comparisonSegment.value) : null;
      let liftComparisonValue;

      if (this.bookmarkParams.liftComparison) {
        const liftComparisonSegment = liftComparisonLookup?.get(segment.key);
        liftComparisonValue = liftComparisonSegment
          ? getSingleNumberFromSegmentValue(liftComparisonSegment.value)
          : null;

        const baselineSegment = getLiftBaselineSegment(
          this.bookmarkParams.liftComparison,
          segment.path,
          segmentLookup,
          comparisonSegment,
        );
        comparisonValue = baselineSegment ? getSingleNumberFromSegmentValue(baselineSegment.value) : 0;
      }

      const {metricUnit} = getMetricLabels({
        bookmarkParams: this.bookmarkParams,
        sectionsMetadata: this.results.meta.report_sections,
        dataGroups: Object.values(this.getDataGroups()),
        count: value,
        key: segment.path[0],
      });

      return {
        value,
        path: segment.path,
        comparisonValue,
        liftComparisonValue,
        label: segment.displayName,
        subLabel: metricUnit,
        color: this.segmentFilterController.getItemColor(0, item.id),
      };
    });
  }

  metricFormatter = (value: number, options: {forDataTable?: boolean} = {forDataTable: false}): string => {
    let formatted = ``;
    if (isFunnelConversionMetric(this.bookmarkParams) || isRetentionConversionRateMetric(this.bookmarkParams)) {
      formatted = formatPercent(value);
    } else if (isFunnelTimeToConvertMetric(this.bookmarkParams)) {
      formatted = formatConciseDuration(value);
    } else if (options.forDataTable) {
      formatted = formatDataTableNumber(value, {isAverage: false});
    } else {
      formatted = formatMetricNumber(value);
    }
    return formatted;
  };
}

export const InsightsMetricChartEl = defineAndMakeJsxFactory<InsightsMetricJsxProps>(
  `mp-insights-metric-chart`,
  InsightsMetricChart,
);
export const InsightsMetricChartReact = panel2react<InsightsMetricJsxProps>(
  `mp-insights-metric-chart`,
  InsightsMetricChart,
);
